<template>
  <div
    :class="[
      'relative inline-flex items-center justify-center flex-shrink-0 overflow-hidden',
      sizeClasses,
      rounded ? 'rounded-full' : 'rounded-lg',
      bgColor
    ]"
    :title="name"
  >
    <!-- Image -->
    <img
      v-if="src && !imageError"
      :src="src"
      :alt="name"
      :class="['w-full h-full object-cover', rounded ? 'rounded-full' : 'rounded-lg']"
      @error="handleImageError"
    />

    <!-- Initials Fallback -->
    <span
      v-else
      :class="['font-semibold uppercase select-none', textSizeClasses, textColor]"
    >
      {{ initials }}
    </span>

    <!-- Online Status Indicator -->
    <span
      v-if="showStatus && online"
      :class="[
        'absolute bottom-0 right-0 block rounded-full ring-2 ring-white dark:ring-gray-800',
        statusSizeClasses,
        online ? 'bg-green-400' : 'bg-gray-400'
      ]"
    ></span>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  src: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: 'User',
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl', '2xl'].includes(value),
  },
  rounded: {
    type: Boolean,
    default: true,
  },
  color: {
    type: String,
    default: null,
  },
  showStatus: {
    type: Boolean,
    default: false,
  },
  online: {
    type: Boolean,
    default: false,
  },
});

const imageError = ref(false);

/**
 * Get size classes based on size prop
 */
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-24 h-24',
  };
  return sizes[props.size];
});

/**
 * Get text size classes for initials
 */
const textSizeClasses = computed(() => {
  const sizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-2xl',
    '2xl': 'text-4xl',
  };
  return sizes[props.size];
});

/**
 * Get status indicator size classes
 */
const statusSizeClasses = computed(() => {
  const sizes = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4',
    '2xl': 'w-5 h-5',
  };
  return sizes[props.size];
});

/**
 * Get initials from name
 */
const initials = computed(() => {
  if (!props.name) return '?';

  const parts = props.name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  return props.name.substring(0, 2).toUpperCase();
});

/**
 * Generate background color based on name
 */
const bgColor = computed(() => {
  if (props.color) return props.color;
  if (props.src && !imageError.value) return 'bg-gray-100 dark:bg-gray-800';

  // Generate consistent color based on name
  const colors = [
    'bg-gradient-to-br from-blue-500 to-blue-600',
    'bg-gradient-to-br from-purple-500 to-purple-600',
    'bg-gradient-to-br from-pink-500 to-pink-600',
    'bg-gradient-to-br from-red-500 to-red-600',
    'bg-gradient-to-br from-orange-500 to-orange-600',
    'bg-gradient-to-br from-yellow-500 to-yellow-600',
    'bg-gradient-to-br from-green-500 to-green-600',
    'bg-gradient-to-br from-teal-500 to-teal-600',
    'bg-gradient-to-br from-cyan-500 to-cyan-600',
    'bg-gradient-to-br from-indigo-500 to-indigo-600',
  ];

  const hash = props.name.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  return colors[Math.abs(hash) % colors.length];
});

/**
 * Get text color (white for gradient backgrounds)
 */
const textColor = computed(() => {
  return 'text-white';
});

/**
 * Handle image loading error
 */
const handleImageError = () => {
  imageError.value = true;
};
</script>
